import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CustomSlider from "../../components/CustomSlider/CustomSlider";
import "./SingleProjectPage.css";
import Sidebar from "../../components/Sidebar/Sidebar";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fetchProjectBySlug = async (slug, apiToken) => {
  const response = await fetch(
    `${API_BASE_URL}/api/single-projects?filters[slug][$eq]=${slug}&populate[Images][fields][0]=url&populate[RelatedProjects][fields][0]=Title&populate[RelatedProjects][fields][1]=slug`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    }
  );
  const data = await response.json();
  return data.data[0];
};

export default function SingleProjectPage({
  apiToken,
  onNavigate,
  onLongNavigate,
  screenSize,
  screenHeight,
  setSliderTheme,
  setIsHeaderVisible,
}) {
  const { slug } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { data: project, isLoading, error } = useQuery({
    queryKey: ["single-project", slug],
    queryFn: () => fetchProjectBySlug(slug, apiToken),
    staleTime: 1000 * 60 * 60 * 24, // 24h
    cacheTime: 1000 * 60 * 60 * 24, // 24h em cache
  });

  useEffect(() => {
    setIsHeaderVisible(true);
  }, [setIsHeaderVisible]);

  // ✅ useEffect sempre fora de return condicional
  useEffect(() => {
    if (project?.attributes?.Theme) {
      setSliderTheme(project.attributes.Theme === "Light" ? "Light" : "Dark");
    }
  }, [project, setSliderTheme]);

  if (isLoading) return <div></div>;
  if (error || !project) return <div>Erro ao carregar projeto</div>;

  const {
    Title,
    Description,
    Details,
    Images,
    RelatedProjects,
    Date: projectDate,
    City,
    Theme,
  } = project.attributes;

  const slides = Images.data.map((image) => ({
    image: `${image.attributes.url}`,
  }));

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const ogImage = slides?.[0]?.image || "https://productive-prosperity-58dbc90efe.media.strapiapp.com/SEINEDESIGN_hotel_flottant_paris_off_1_3296f3d699.jpg"
  const ogUrl = typeof window !== "undefined" ? window.location.href : `https://ronzatti.com/projects/${slug}`

  return (
    <>
    <Helmet>
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={ogUrl} />
        <link rel="canonical" href={ogUrl} />
        <title>
          {`${Title} - Projets d'Architecture Flottante`}
        </title>
        <meta
          name="description"
          content={`${Title} - Explorez nos réalisations innovantes en architecture navale et flottante. Découvrez les projets phares de Seine Design, des hôtels aux galeries d'art sur l'eau, et laissez-vous inspirer.`}
        />
        <meta
          property="og:title"
          content={`${Title} - Projets d'Architecture Flottante`}
        />
        <meta
          property="og:description"
          content={`${Title} - Explorez nos réalisations innovantes en architecture navale et flottante. Découvrez les projets phares de Seine Design, des hôtels aux galeries d'art sur l'eau, et laissez-vous inspirer.`}
        />
        <meta
          property="og:image"
          content={ogImage}
        />
      </Helmet>
    <div
      className={`single-project-container ${
        isSidebarOpen ? "menuIsOpen" : "menuIsClose"
      } ${Theme === "Light" ? "themeLight" : "themeDark"}`}
    >
      <div id="overlay-transition">
        <h1>{Title}</h1>
        <h3>{City}</h3>
        <h6>{new Date(projectDate).getFullYear()}</h6>
      </div>
      <div id="overlay-transition-2"></div>
      <div className="project-slider">
        <CustomSlider
          slides={slides}
          enableDrag={true}
          isSidebarOpen={isSidebarOpen}
          screenSize={screenSize}
          screenHeight={screenHeight}
        />
      </div>
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        onNavigate={onNavigate}
        onLongNavigate={onLongNavigate}
        title={Title}
        description={Description}
        details={Details}
        relatedProjects={
          RelatedProjects
            ? RelatedProjects.data.map((proj) => ({
                id: proj.id,
                slug: proj.attributes.slug,
                Title: proj.attributes.Title,
              }))
            : []
        }
      />
    </div>
    </>
  );
}
